import { Component, OnDestroy, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { AnnouncementSlugs, CardTypeCallToActions, RouteTypes } from '@app/enums/_index';
import { AnnouncementService, CertificationService } from '@app/services/_index';
import { calculateExpirationDate, getAdditionalFieldValue } from '@app/utilities/_index';
import { Subscription, firstValueFrom } from 'rxjs';

import { StaticPageAdapter } from '@app/adapters/_index';
import { SessionStorageKeys } from '@app/enums/session-storage-keys.enum';
import { StaticPageInterface } from '@app/interfaces/_index';
import { StaticPage } from '@app/models/_index';
import { Certificate } from '@app/models/certifications.model';
import { User } from '@app/models/user.model';
import { UserService } from '@app/services/user/user.service';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-message-inbox',
  templateUrl: './message-inbox.component.html',
  styleUrls: ['./message-inbox.component.scss'],
  standalone: false,
})
export class MessageInboxComponent implements OnInit, OnDestroy {
  isLoading: boolean;
  todayDate: number;
  currentUser: User; // Signed-in User
  sfdcID: string; // Sales Force User Id

  certificates: Certificate[] = []; // All certificates
  expiredCertificates: Certificate[] = []; // All Expired Certificates
  futureExpirationCertificates: Certificate[] = []; // All Future Expired Certificates
  subs: Subscription[] = [];
  messageInboxNoRecordsAnnouncement: StaticPage;

  constructor(
    private userService: UserService,
    private certificateService: CertificationService,
    private metaService: Meta,
    private titleService: Title,
    private staticPageAdapter: StaticPageAdapter,
    private announcementService: AnnouncementService
  ) {}

  /**
   * Oninit loads the user and loads expired certificate as a message information banners
   */
  async ngOnInit(): Promise<void> {
    // Subscribe to logged in user
    this.currentUser = await firstValueFrom(this.userService?.doceboUser$);

    // Get current date
    this.todayDate = Date.now();
    // Set title and metadata tags
    this.setTitleAndMetaData();
    // Load expired certifications, if any
    this.loadExpiredCertificates();
    // Message Inbox No Records from CMS
    this.getMessageInboxNoRecordsAnnouncement();
  }

  /**
   * Loads Expired Certificates for a given user
   *  Expiring certificates will show if they are within 60 days or 2 months. Most
   *  certificates will expire in years, so there maybe no expired certifiates that may show for years.
   */
  loadExpiredCertificates(): void {
    const sfdcid = getAdditionalFieldValue(
      environment.docebo.additional_fields.salesforce_user_id_override,
      this.currentUser
    )
      ? getAdditionalFieldValue(
          environment.docebo.additional_fields.salesforce_user_id_override,
          this.currentUser
        )
      : getAdditionalFieldValue(
          environment.docebo.additional_fields.salesforce_user_id,
          this.currentUser
        );
    localStorage.removeItem(SessionStorageKeys.CERTIFICATES);

    this.certificateService
      .getCertificates(sfdcid)
      .then((certificates: Certificate[]) => {
        this.certificates = certificates;
        // Load Certificates in cache
        localStorage.setItem(SessionStorageKeys.CERTIFICATES, JSON.stringify(this.certificates));
        this.certificates.forEach((certificateItem: Certificate) => {
          const diffDays = calculateExpirationDate(certificateItem.expires);
          // Set certificate expired flag and expiryDueDate and push to expiredCertificated []
          if (diffDays < 60 && diffDays >= 0) {
            certificateItem.expiredFlag = true;
            certificateItem.expiryDueDate = diffDays;
            // Push expired arrays
            this.expiredCertificates.push(certificateItem);
          } else {
            certificateItem.expiredFlag = false;
            // Push future pending certificates that will expire in future
            certificateItem.expiryDueDate = diffDays;
            this.futureExpirationCertificates.push(certificateItem);
          }
        });
      })
      .catch((err) => {
        console.log('Unable to load expired certifications', err);
      });
  }

  /**
   * Add Track By Certification title to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of Certification Array
   * @param item -  item
   * @returns title of certification
   */
  trackByMessageInboxTitle(index: number, item: Certificate): string {
    if (item) {
      return item.title;
    } else {
      return String(index);
    }
  }

  /**
   * Set selected subscription title and metadata tags
   */
  setTitleAndMetaData(): void {
    // NOTE: Update page title on user dashboard page
    this.titleService.setTitle('Message Inbox | Genesys Education');
    // NOTE: Update meta tag description for page
    this.metaService.updateTag({
      name: 'description',
      content:
        'See all of your Genesys Education enrollments, certifications, and recommended learning in one place.',
    });
  }

  /**
   * Get the Message Inbox Announcement content.
   */
  getMessageInboxNoRecordsAnnouncement(): void {
    this.isLoading = true;
    const wordpressSlugName = AnnouncementSlugs.MESSAGE_INBOX_NORECORDS_SLUG;
    this.subs.push(
      this.announcementService.getAnnouncementContentBySlug(wordpressSlugName).subscribe({
        next: (response: StaticPageInterface[]) => {
          if (response && response.length > 0) {
            this.messageInboxNoRecordsAnnouncement = this.staticPageAdapter.adapt(response[0]);
            this.isLoading = false;
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Message Inbox content completed');
        },
      })
    );
  }

  /**
   *  CardTypeCallToActions enums based on card types
   */
  public get cardTypeCallToActions(): typeof CardTypeCallToActions {
    return CardTypeCallToActions;
  }

  /**
   *  RouteTypes enums based on route types
   */
  public get routeTypes(): typeof RouteTypes {
    return RouteTypes;
  }

  /**
   * On Destroy, unsubscribe subscriptions to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }
}
